import store from '../../state';
import { selectCurrentDashboard } from '../../state/modules/dashboards/selectors';
import dashboardOperations from '../../state/modules/dashboards/operations';
import { operations as groupOperations } from '../../state/modules/groups';
import { getAnnouncements } from '../../state/modules/announcements/operations';
import { operations as skillOperations } from '../../state/modules/skills';
import { operations as availabilityCodeOperations } from '../../state/modules/availability-codes';
import { operations as userOperations } from '../../state/modules/users';
import { operations as membershipOperations } from '../../state/modules/memberships';
import { operations as sbdfOperations } from '../../state/modules/standby-duty-functions';
import { operations as taskOperations } from '../../state/modules/tasks';
import { operations as dutyTransactionOperations } from '../../state/modules/duty-transactions';
import { operations as activityTypeOperations } from '../../state/modules/activity-types';
import { operations as incidentOperations } from '../../state/modules/incidents';
import { operations as activityReportOperations } from '../../state/modules/activity-reports';
import DashboardRegistrationManager from './DashboardRegistrationManager';
import { getAccessScopeGroupIds, getAuthHeader } from './utils';

let instance;

// The DashboardAPIManager is responsible for injecting the right authentication
// credentials in each request. Depending on whether it is available, it injects
// the dashboardReadAuthToken into every request. This token gives accesss to
// regional information, instead of defaulting to the scope of information
// available to the current user. This is needed to show regional widgets, such
// as the ContactListWidget for roles throughout the region.
export default class DashboardAPIManager {
  constructor() {
    if (instance) return instance;
    instance = this;

    this.lastWSConnectionAt = null;
    this.authTokenRetries = 0;
    store.subscribe(this.handleChanged);
  }

  setDefaultGroupId = groupId => {
    this.defaultGroupId = groupId;
  };

  resetSubscriptions = () => new DashboardRegistrationManager().resetSubscriptions();

  // poor man's useEffect
  handleChanged = async () => {
    if (this.activePromise) {
      await this.activePromise;
      return;
    }

    const token = store.getState().dashboards.dashboardReadAuthToken || null;
    const currentDashboardId = this.getCurrentDashboardId();
    const currentDashboardAccessScopeGroupIds = this.getCurrentDashboardAccessScopeGroupIds();

    // Check if there is no token, there should be one, and the requestedAuthToken flag is set to true
    if (token === null && currentDashboardAccessScopeGroupIds.length > 0 && this.requestedAuthToken) {
      this.authTokenRetries += 1;
      if (this.authTokenRetries <= 5) this.requestedAuthToken = false;
    }

    // as soon as there is a current dashboard, request an auth token for it
    if (currentDashboardId && !this.requestedAuthToken) {
      // If there are no access scope group IDs, there is no need to request
      // a token, because the dashboard will show data according to the user's access
      if (currentDashboardAccessScopeGroupIds.length === 0) {
        this.requestedAuthToken = true;
        return;
      }

      // To prevent multiple requests at the same time, we wrap the request inside a promise
      // and store it. This way, when the next handleChanged is called,
      // it will wait for the promise to resolve before trying to request another token.
      this.activePromise = (async () => {
        try {
          await store.dispatch(dashboardOperations.getDashboardAuthToken(currentDashboardId));
        } catch (error) {
          console.error('Failed fetching dashboard token', error);
          this.requestedAuthToken = false;
        } finally {
          this.requestedAuthToken = true;
        }
      })();

      // Await the execution block and clear the lock when entirely finished
      try {
        await this.activePromise;
      } finally {
        this.activePromise = null;
      }
    }
    // Handle stale schedules: when schedules are stale, do a reset of WS subscriptions.
    // Restarting a WS subscription triggers an initial API fetch automatically
    const staleCombinedSchedules = store.getState().combinedSchedules.full.stale;

    if (staleCombinedSchedules) {
      console.log('Stale combined schedules detected, resetting WS subscriptions.');
      this.resetSubscriptions();
    }
  };

  registerForAvailabilityRequirementScheduleUpdates = params =>
    new DashboardRegistrationManager().registerForAvailabilityRequirementScheduleUpdates(params);

  registerForIncidentUpdates = params => new DashboardRegistrationManager().registerForIncidentUpdates(params);

  registerForCombinedScheduleUpdates = params =>
    new DashboardRegistrationManager().registerForCombinedScheduleUpdates(params);

  registerForDashboardUpdates = params => new DashboardRegistrationManager().registerForDashboardUpdates(params);

  getCurrentDashboardId = () => this.getCurrentDashboard()?.id;

  getCurrentDashboard = () => selectCurrentDashboard(store.getState());

  getCurrentDashboardAccessScopeGroupIds = () => this.getCurrentDashboard()?.access_scope_group_ids || [];

  isTemplate = () => this.getCurrentDashboard()?.system_template;

  // This function returns a function which dispatches an action and injects the
  // authentication token if available. It simplifies the creation of functions
  // below which dispatch a redux action and adds the authentication header.
  dispatchifyWithGroupId = fn => async args => {
    if (this.isTemplate()) {
      // don't load data for templates. This is unnecessary and takes a ton of
      // time
      return;
    }

    let { params, headers, ...rest } = args || {};
    params = params || {};

    // inject the dashboard auth header, which may give access to e.g. regional
    // information, even though we're at the level of a Station
    // This is deliberately done before getting AccessScopeGroupIds, because
    // when the token is available, the access groups ID will as well
    headers = { ...headers, ...(await getAuthHeader()) };

    // inject the current group IDs so queries are limited to the objects
    // related to the access scope groups. Note that for regional dashboards,
    // the access scope group IDs may not the Station IDs, but the region IDs.
    // This is why we're requesting all objects in the hierarchy. For example,
    // this will load all Announcements in the region, and not only regional
    // announcements.
    let group_ids = (await getAccessScopeGroupIds()) || this.defaultGroupId || params.group_ids;
    group_ids = [group_ids].flat().join(',');
    params = {
      ...params,
      group_ids,
      in_hierarchy: true
    };

    return store.dispatch(fn({ params, headers, ...rest }));
  };

  fetchGroups = this.dispatchifyWithGroupId(groupOperations.fetch);
  fetchSkills = this.dispatchifyWithGroupId(skillOperations.fetch);
  fetchUsers = this.dispatchifyWithGroupId(userOperations.fetch);
  fetchAvailabilityCodes = this.dispatchifyWithGroupId(availabilityCodeOperations.fetch);
  fetchDutyTransactions = this.dispatchifyWithGroupId(dutyTransactionOperations.fetch);
  fetchMemberships = this.dispatchifyWithGroupId(membershipOperations.fetch);
  fetchStandbyDutyFunctions = this.dispatchifyWithGroupId(sbdfOperations.fetch);
  fetchTasks = this.dispatchifyWithGroupId(taskOperations.fetch);
  getAnnouncements = this.dispatchifyWithGroupId(getAnnouncements);
  fetchActivityTypes = this.dispatchifyWithGroupId(activityTypeOperations.fetch);
  fetchIncidents = this.dispatchifyWithGroupId(incidentOperations.fetch);
  fetchActivityReports = this.dispatchifyWithGroupId(activityReportOperations.fetch);

  // this does not take the normal set of params, it uses positional params
  fetchGroupAndSubgroups = async groupId => {
    // deliberately get the auth header first, because this ensures that the
    // access scope group IDs are available too
    const header = await getAuthHeader();
    groupId = (await getAccessScopeGroupIds()) || this.defaultGroupId || groupId;
    return store.dispatch(groupOperations.fetchGroupAndSubgroups(groupId, header));
  };
}
